import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { GeoBrowseStudy } from '../../types';
import { useGeoBrowseContext } from '../../context/GeoBrowseContext';
import { useGeoBrowseQuery } from '../../hooks/useGeoBrowseThresholds';
import { fetchStudies } from '../../services/geoBrowseApi';
import { formatNumber } from '../../utils/formatters';
import { buildStudyDetailPath, comparisonRowKey } from '../../utils/studyHelpers';
import BrowseDataTable, { type BrowseColumn } from '../BrowseDataTable';
import PageHeader from '../PageHeader';
import { InlineBar } from '../ui/GeoBrowseUi';
import styles from '../../GeoBrowse.module.css';

export default function StudiesView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cuiFilter = searchParams.get('cui') ?? undefined;
  const { thresholds, refreshKey, buildPathWithThresholds } = useGeoBrowseContext();
  const { data, loading, error } = useGeoBrowseQuery(
    () => fetchStudies(thresholds, cuiFilter),
    [thresholds.padj, thresholds.fc, refreshKey, cuiFilter],
  );

  const maxGenesSig = useMemo(
    () => Math.max(1, ...(data ?? []).map(s => s.n_genes_sig)),
    [data],
  );

  const columns: BrowseColumn<GeoBrowseStudy>[] = useMemo(
    () => [
      {
        id: 'cui',
        label: 'CUI',
        sortValue: r => r.cui,
        accessor: r => r.cui,
      },
      {
        id: 'accession',
        label: 'Accession',
        sortValue: r => r.accession,
        accessor: r => r.accession,
      },
      {
        id: 'comparison_id',
        label: 'Comparison',
        sortValue: r => r.comparison_id,
        accessor: r => r.comparison_id,
      },
      {
        id: 'title',
        label: 'Title',
        sortValue: r => r.title,
        accessor: r => r.title,
        render: r => (
          <span className={styles.studyTitle} title={r.title}>
            {r.title}
          </span>
        ),
      },
      {
        id: 'overall_design',
        label: 'Overall design',
        sortValue: r => r.overall_design,
        accessor: r => r.overall_design || '—',
      },
      {
        id: 'year',
        label: 'Year',
        sortValue: r => r.year,
        accessor: r => r.year,
      },
      {
        id: 'n_samples',
        label: 'Samples',
        sortValue: r => r.n_samples,
        accessor: r => r.n_samples,
      },
      {
        id: 'n_case',
        label: 'Cases',
        sortValue: r => r.n_case,
        accessor: r => r.n_case,
      },
      {
        id: 'n_control',
        label: 'Controls',
        sortValue: r => r.n_control,
        accessor: r => r.n_control,
      },
      {
        id: 'usable_for_deg',
        label: 'Usable for DEG',
        sortValue: r => (r.usable_for_deg ? 1 : 0),
        accessor: r => String(r.usable_for_deg),
        render: r =>
          r.usable_for_deg ? (
            <CheckIcon className={styles.booleanIconTrue} aria-label="Yes" title="Yes" />
          ) : (
            <XMarkIcon className={styles.booleanIconFalse} aria-label="No" title="No" />
          ),
      },
      {
        id: 'has_deg',
        label: 'Has DEG',
        sortValue: r => (r.has_deg ? 1 : 0),
        accessor: r => String(r.has_deg),
        render: r =>
          r.has_deg ? (
            <CheckIcon className={styles.booleanIconTrue} aria-label="Yes" title="Yes" />
          ) : (
            <XMarkIcon className={styles.booleanIconFalse} aria-label="No" title="No" />
          ),
      },
      {
        id: 'n_genes_tested',
        label: 'Genes tested',
        sortValue: r => r.n_genes_tested,
        accessor: r => formatNumber(r.n_genes_tested),
      },
      {
        id: 'n_genes_sig',
        label: 'Significant genes',
        sortValue: r => r.n_genes_sig,
        filterValue: r => r.n_genes_sig,
        render: r => <InlineBar value={r.n_genes_sig} max={maxGenesSig} tone="blue" />,
      },
    ],
    [maxGenesSig],
  );

  if (loading) return <div className={styles.loading}>Loading studies…</div>;
  if (error) return <div className={styles.error}>{error}</div>;
  if (!data?.length) return <div className={styles.empty}>No studies found.</div>;

  return (
    <>
      <PageHeader
        title="Studies"
        description="Click a row to see its full metadata + volcano plot in Study Detail."
      />

      <BrowseDataTable
        rows={data}
        columns={columns}
        rowKey={r => comparisonRowKey(r)}
        searchPlaceholder=""
        defaultPageSize={25}
        defaultSort={{ columnId: 'n_genes_sig', direction: 'desc' }}
        searchFilter={(row, q) =>
          `${row.title} ${row.accession} ${row.comparison_id} ${row.cui} ${row.overall_design}`.toLowerCase().includes(q)
        }
        onRowClick={row =>
          navigate(
            buildPathWithThresholds(buildStudyDetailPath(row.cui, row.accession, row.comparison_id)),
          )
        }
      />
    </>
  );
}
